import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  GlobalStyles,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation, Trans } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";

import ChatBoxesIcon from "../icons/ChatBoxesIcon";
import CallIcon from "../icons/CallIcon";
import ChatDialogueIcon from "../icons/ChatDialogueIcon";
import TextIcon from "../icons/TextIcon";
import ASLIcon from "../icons/ASLIcon";
import ArrowRightIcon from "../icons/ArrowRightIcon";

import { replaceMainSiteLinkHostname } from "../../services/Utils";
import { useScrollToTop } from "../../hooks/useScrollToTop";

const TriagePage = () => {
  const { t, i18n } = useTranslation("postChat");
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang");
  const query = lang === "es" ? "?lang=es" : "";
  const isEnglish = i18n?.resolvedLanguage?.startsWith("en");

  const VITE_CHAT_HOST =
    import.meta.env.VITE_CHAT_HOST || "https://chat.988lifeline.org";
  const VITE_SMS_NUMBER = import.meta.env.VITE_SMS_NUMBER || "988";
  const VITE_CALL_NUMBER = import.meta.env.VITE_CALL_NUMBER || "988";

  useScrollToTop();

  const { chatId } = useParams();

  const pillBtnSx = {
    textTransform: "none",
    borderRadius: 999,
    fontSize: "20px",
    minWidth: { xs: 120, sm: 130, md: 150 },
    fontFamily: "Public Sans, sans-serif",
    fontWeight: 700,
    px: { xs: 1.5, md: 2 },
    bgcolor: theme.palette.primary.main,
    color: theme.palette.white,
    "&:hover": { bgcolor: theme.palette.secondary.dark },
    "& .MuiButton-startIcon": {
      mr: 1,
      "& svg": { fontSize: { xs: 20, md: 24 } },
    },
  };

  const linkStyles = (theme) => ({
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: "16px",
    fontFamily: "Public Sans, sans-serif",
    textDecoration: "underline",
    textUnderlineOffset: "4px",
    textDecorationThickness: "1px",
    outline: "1px dashed transparent",
    p: "1px",
    ":focus": {
      outline: `1px dashed ${theme.palette.secondary.main}`,
    },
    "&:hover": {
      color: theme.palette.tertiary.main,
      textDecorationColor: theme.palette.tertiary.main,
    },
  });

  return (
    <>
      <GlobalStyles
        styles={{
          html: { backgroundColor: theme.palette.white },
          body: { backgroundColor: theme.palette.white },
        }}
      />
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant="h2"
          sx={{
            textAlign: "center",
            mb: isDesktop ? 0.5 : 1,
            color: theme.palette.secondary.main,
            fontSize: "25px",
            pl: { xs: 0, lg: "50px" },
          }}
        >
          {t("triagePage.header")}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: theme.palette.secondary.dark,
            mb: { xs: 4, md: 5 },
            fontWeight: 400,
            lineHeight: "180%",
            fontSize: "16px",
            pl: { xs: 0, lg: "50px" },
          }}
        >
          {t("triagePage.subHeader")}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            mx: "auto",
            p: { xs: 2.5, md: 4 },
            borderRadius: 3,
            backgroundColor: theme.palette.lightPurple,
            maxWidth: { xs: "calc(100% - 50px)", lg: 1280 },
            width: { xs: "calc(100% - 50px)", lg: "100%" },
          }}
        >
          <ChatBoxesIcon sx={{ fontSize: 35, display: "block", mb: 1 }} />

          <Typography
            variant="h4"
            sx={{
              mb: 0.5,
              fontSize: "25px",
              lineHeight: "140%",
              textAlign: "left",
            }}
          >
            {t("triagePage.startAnotherConversation")}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: { xs: 2.5, md: 3 },
              fontSize: "16px",
              lineHeight: "180%",
              textAlign: "left",
            }}
          >
            {t("triagePage.wereHere")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              columnGap: { xs: 2.5, sm: 2.5, md: 3 },
              rowGap: { xs: 2, sm: 1 },
            }}
          >
            <Button
              component="a"
              href={`tel:${VITE_CALL_NUMBER}`}
              variant="contained"
              disableRipple
              startIcon={<CallIcon sx={{ width: 28, height: 28 }} />}
              sx={{ ...pillBtnSx, justifySelf: "center" }}
            >
              {t("triagePage.call")}
            </Button>

            <Button
              component="a"
              href={`sms:${VITE_SMS_NUMBER}`}
              variant="contained"
              disableRipple
              startIcon={<TextIcon sx={{ width: 28, height: 28 }} />}
              sx={{ ...pillBtnSx, justifySelf: "center" }}
            >
              {t("triagePage.text")}
            </Button>

            <Button
              component="a"
              href={replaceMainSiteLinkHostname(
                t("chatHomeUrl", { ns: "common" }),
                VITE_CHAT_HOST,
              )}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              disableRipple
              startIcon={
                <ChatDialogueIcon
                  fill="white"
                  sx={{ width: 22, height: 22, pb: 0.5 }}
                />
              }
              sx={{
                ...pillBtnSx,
                gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "auto" },
                justifySelf: "center",
              }}
            >
              {t("triagePage.chat")}
            </Button>

            {isEnglish && (
              <Button
                component="a"
                href={replaceMainSiteLinkHostname(t("triagePage.deafHohLink"))}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                disableRipple
                startIcon={<ASLIcon sx={{ width: 27, height: 27 }} />}
                aria-label={t("navigationMenu.aslAriaLabel", { ns: "common" })}
                sx={{
                  ...pillBtnSx,
                  gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "auto" },
                  justifySelf: "center",
                }}
              >
                {t("triagePage.deafHoh")}
              </Button>
            )}
          </Box>
        </Paper>

        <Box
          sx={{
            mt: 2.5,
            mx: "auto",
            width: "100%",
            maxWidth: { xs: "none", lg: 1280 },
            display: "flex",
            justifyContent: "flex-end",
            pr: { xs: 0, md: "150px" },
          }}
        >
          <Link
            underline="none"
            href={`/feedback/${chatId}/thank-you${query}`}
            sx={{
              ...linkStyles(theme),
              display: "inline-flex",
              alignItems: "center",
              whiteSpace: "nowrap",
              gap: 1,
            }}
          >
            {t("triagePage.exploreResources")}
            <ArrowRightIcon sx={{ fontSize: 28, pb: "15px" }} />
          </Link>
        </Box>

        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            mt: { xs: 6, md: 7 },
            color: theme.palette.secondary.dark,
            fontSize: "16px",
            lineHeight: "180%",
            pl: { xs: 0, lg: "75px" },
          }}
        >
          <Trans
            i18nKey="triagePage.contactUsPageLink"
            ns="postChat"
            components={{
              contactLink: (
                <Link
                  href={replaceMainSiteLinkHostname(
                    t("thankYouPage.contactUsPageLink"),
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  sx={{
                    ...linkStyles(theme),
                    display: "inline-flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    gap: 0.5,
                  }}
                />
              ),
            }}
          />
        </Typography>
      </Box>
    </>
  );
};

TriagePage.propTypes = {
  onExplore: PropTypes.func,
};

export default TriagePage;
